import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, MapPin, Ticket as TicketIcon, Clock, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { useBookingsStore } from '@/lib/stores/bookings-store';
import { useNavigate } from 'react-router-dom';
import { TicketList } from '@/components/tickets/TicketList';
import { QRCodeDisplay } from '@/components/tickets/QRCodeDisplay';
import type { Ticket } from '@/types/tickets';
import { supabase } from '@/lib/supabase/client';
import { ContactCreatorModal } from '@/components/creator/ContactCreatorModal';
import { logger } from '@/lib/utils/logger';

export default function Bookings() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useUnifiedAuth();
  const { bookings, fetchUserBookings } = useBookingsStore();
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past' | 'cancelled'>('all');
  const [showContactModal, setShowContactModal] = useState(false);
  const [creatorContact, setCreatorContact] = useState({ email: '', instagram: '', phone: '' });
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [bookingTickets, setBookingTickets] = useState<Record<string, Ticket[]>>({});
  const [expandedBookings, setExpandedBookings] = useState<Set<string>>(new Set());
  const [transferredTickets, setTransferredTickets] = useState<any[]>([]);
  const [pendingTransfers, setPendingTransfers] = useState<any[]>([]);
  const [claimingTicket, setClaimingTicket] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancellingTransfer, setCancellingTransfer] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      logger.debug('Current user:', { email: user.email, id: user.id });
      fetchUserBookings(user.id);
    }
  }, [user, fetchUserBookings]);

  // Fetch tickets for confirmed bookings only
  useEffect(() => {
    const fetchTickets = async () => {
      // Only fetch tickets for confirmed/paid bookings - exclude pending
      const confirmedBookings = bookings.filter(b => b.status === 'confirmed');
      
      if (confirmedBookings.length === 0) return;
      
      logger.debug(`Fetching tickets for ${confirmedBookings.length} confirmed bookings`);
      
      const ticketPromises = confirmedBookings.map(async (booking) => {
        // Get all tickets for this booking (owned by the user)
        const { data, error } = await supabase
          .from('tickets')
          .select('*')
          .eq('booking_id', booking.id)
          .order('ticket_number');
        
        if (error) {
          logger.error(`Error fetching tickets for booking ${booking.id}:`, error);
        }
        
        if (!error && data) {
          logger.debug(`Found ${data.length} tickets for booking ${booking.id}`);
          return { bookingId: booking.id, tickets: data };
        }
        return null;
      });
      
      const results = await Promise.all(ticketPromises);
      const ticketMap: Record<string, Ticket[]> = {};
      
      results.forEach(result => {
        if (result) {
          ticketMap[result.bookingId] = result.tickets;
        }
      });
      
      logger.debug(`Tickets fetched successfully: ${Object.keys(ticketMap).length} bookings with tickets`);
      setBookingTickets(ticketMap);
    };
    
    // Only fetch tickets if we have bookings and user is authenticated
    if (user?.id && bookings.length > 0) {
      fetchTickets();
    }
  }, [bookings, user]);

  // Function to fetch allocated/transferred tickets
  const fetchAllocatedTickets = useCallback(async () => {
    if (!user?.id || !user?.email) {
      logger.debug('User not ready, skipping allocated tickets fetch');
      return;
    }
    
    logger.debug('Fetching allocated/transferred tickets for:', user.email);
    
    // Query tickets table directly - completely separate from bookings
    // This gets all tickets where the current user is the guest
    const { data: tickets = [], error } = await supabase
      .from('tickets')
      .select(`
        *,
        experience:experiences!tickets_experience_id_fkey (
          id,
          title,
          description,
          location,
          status,
          media:experience_media (
            url,
            type,
            order_index
          )
        )
      `)
      .eq('guest_email', user.email)
      .not('transfer_type', 'is', null); // Only get tickets with transfer_type set
    
    logger.debug('Tickets query result:', { 
      count: tickets?.length, 
      error,
      tickets: tickets?.map(t => ({ 
        id: t.id, 
        transfer_type: t.transfer_type,
        claimed: !!t.claimed_by 
      }))
    });
    
    if (error) {
      logger.error('Error fetching allocated/transferred tickets:', error);
      setTransferredTickets([]);
      return;
    }
    
    // Process tickets based on transfer_type
    const processedTickets = (tickets || []).map(ticket => ({
      ...ticket,
      ticketType: ticket.transfer_type, // Keep original value: 'transfer' or 'allocation'
      isClaimedByMe: ticket.claimed_by === user.id
    }));
    
    logger.debug(`Processed ${processedTickets.length} tickets`);
    setTransferredTickets(processedTickets);
  }, [user]);

  // Fetch allocated/transferred tickets on user change
  useEffect(() => {
    logger.debug('Allocated tickets effect running', { userId: user?.id, userEmail: user?.email });
    fetchAllocatedTickets();
  }, [user, fetchAllocatedTickets]);

  // Fetch pending transfer requests
  useEffect(() => {
    if (!user?.id) return;

    const fetchPendingTransfers = async () => {
      logger.debug('Fetching pending transfer requests for user:', user.id);
      
      const { data, error } = await supabase
        .from('ticket_transfers')
        .select(`
          *,
          ticket:tickets!ticket_transfers_ticket_id_fkey (
            id,
            ticket_number,
            experience_id,
            booking_id,
            guest_name,
            guest_email,
            experience:experiences!experience_id (
              id,
              title,
              location
            )
          )
        `)
        .eq('from_user_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error fetching pending transfers:', error);
        setPendingTransfers([]);
        return;
      }

      logger.debug(`Pending transfers found: ${data?.length || 0}`);
      setPendingTransfers(data || []);
    };

    fetchPendingTransfers();
  }, [user]);

  const claimTicket = async (ticketId: string) => {
    if (!user?.id) return;
    
    setClaimingTicket(ticketId);
    
    try {
      const { error } = await supabase
        .from('tickets')
        .update({
          claimed_by: user.id,
          claimed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', ticketId)
        .eq('guest_email', user.email); // Safety check
        
      if (error) {
        logger.error('Error claiming ticket:', error);
        alert('Failed to claim ticket. Please try again.');
      } else {
        logger.info('Ticket claimed successfully!');
        
        // Refresh the lists
        if (user.id) {
          fetchUserBookings(user.id);
        }
        
        // Refresh allocated tickets by calling the fetch function
        await fetchAllocatedTickets();
      }
    } finally {
      setClaimingTicket(null);
    }
  };

  const toggleBooking = (bookingId: string) => {
    const newExpanded = new Set(expandedBookings);
    if (newExpanded.has(bookingId)) {
      newExpanded.delete(bookingId);
    } else {
      newExpanded.add(bookingId);
    }
    setExpandedBookings(newExpanded);
  };

  const cancelTransfer = async (transferId: string) => {
    if (!confirm('Are you sure you want to cancel this transfer request?')) {
      return;
    }
    
    setCancellingTransfer(transferId);
    try {
      const { error } = await supabase
        .from('ticket_transfers')
        .update({
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', transferId)
        .eq('from_user_id', user?.id); // Safety check - only requester can cancel
        
      if (error) throw error;
      
      // Refresh the list
      setPendingTransfers(prev => prev.filter(t => t.id !== transferId));
      
    } catch (err) {
      logger.error('Error cancelling transfer:', err);
      alert('Failed to cancel transfer request');
    } finally {
      setCancellingTransfer(null);
    }
  };

  const filteredBookings = bookings.filter(booking => {
    // Only show confirmed/paid bookings - exclude pending and cancelled
    if (filter === 'all') return booking.status === 'confirmed';
    if (filter === 'upcoming') {
      return booking.status === 'confirmed' && new Date(booking.experience?.start_date || '') >= new Date();
    }
    if (filter === 'past') {
      return booking.status === 'confirmed' && new Date(booking.experience?.start_date || '') < new Date();
    }
    if (filter === 'cancelled') {
      return booking.status === 'cancelled'; // Keep cancelled filter for users who want to see them
    }
    return booking.status === 'confirmed';
  });

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-earth-900 to-black text-sand-100 flex items-center justify-center">
        <p className="text-sand-400">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-earth-900 to-black text-sand-100 flex items-center justify-center">
        <p className="text-sand-400">Please sign in to view your bookings</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-earth-900 to-black text-sand-100 pt-20">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/profile')}
                className="mb-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Profile
              </Button>
              <h1 className="text-2xl sm:text-3xl font-display">My Bookings & Tickets</h1>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium transition-all ${
                filter === 'all' 
                  ? 'bg-sand-400 text-earth-900' 
                  : 'bg-transparent text-sand-400 border border-sand-400/30 hover:border-sand-400/50'
              } rounded-lg`}
            >
              ALL
            </button>
            <button
              onClick={() => setFilter('upcoming')}
              className={`px-4 py-2 text-sm font-medium transition-all ${
                filter === 'upcoming' 
                  ? 'bg-sand-400 text-earth-900' 
                  : 'bg-transparent text-sand-400 border border-sand-400/30 hover:border-sand-400/50'
              } rounded-lg`}
            >
              UPCOMING
            </button>
            <button
              onClick={() => setFilter('past')}
              className={`px-4 py-2 text-sm font-medium transition-all ${
                filter === 'past' 
                  ? 'bg-sand-400 text-earth-900' 
                  : 'bg-transparent text-sand-400 border border-sand-400/30 hover:border-sand-400/50'
              } rounded-lg`}
            >
              PAST
            </button>
            <button
              onClick={() => setFilter('cancelled')}
              className={`px-4 py-2 text-sm font-medium transition-all ${
                filter === 'cancelled' 
                  ? 'bg-sand-400 text-earth-900' 
                  : 'bg-transparent text-sand-400 border border-sand-400/30 hover:border-sand-400/50'
              } rounded-lg`}
            >
              CANCELLED
            </button>
          </div>

          <div className="space-y-6">
            {/* Original Bookings */}
            {filteredBookings.map((booking, index) => {
              const experience = booking.experience;
                            
              if (!experience) return null;

              const firstImage = experience.media?.[0];
              const coverImage = firstImage?.type === 'image' ? firstImage.url : '/images/placeholder.jpg';
              
              const tickets = bookingTickets[booking.id] || [];
              const hasTickets = booking.status === 'confirmed' && tickets.length > 0;

              return (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-earth-800/50 rounded-xl overflow-hidden border border-earth-700"
                >
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Image */}
                      <div className="w-full md:w-60 h-40 rounded-lg overflow-hidden">
                        <img
                          src={coverImage}
                          alt={experience.title}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 space-y-4">
                        <div>
                          <h3 className="text-xl font-display mb-2">{experience.title}</h3>
                          {booking.status === 'confirmed' && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                              Confirmed
                            </span>
                          )}
                        </div>

                        <p className="text-sand-300 line-clamp-2">{experience.description}</p>

                        {/* Location */}
                        {experience.location && (
                          <div className="flex items-center gap-2 text-sm text-sand-400">
                            <MapPin className="h-4 w-4" />
                            <span>{experience.location.city || experience.location.name}</span>
                          </div>
                        )}

                        {/* Contact Creator */}
                        {experience.creator && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-4 w-4 text-sand-400" />
                            <span className="text-sand-400">Contact Creator:</span>
                            <button
                              onClick={() => {
                                setCreatorContact({
                                  email: experience.creator?.creator_profile?.contact_email || 'info@letsrendezvous.co',
                                  instagram: experience.creator?.creator_profile?.social_links?.instagram || 'https://www.instagram.com/letsrendez.vous/',
                                  phone: '',
                                });
                                setShowContactModal(true);
                              }}
                              className="text-sand-100 underline"
                            >
                              {experience.creator.creator_profile?.business_name || experience.creator.full_name || 'Creator'}
                            </button>
                          </div>
                        )}

                        {/* Price */}
                        <div className="text-lg">
                          <span className="text-sand-400">Total Price</span>
                          <p className="text-2xl font-bold text-sand-100">
                            €{booking.total_amount}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 pt-6 border-t border-earth-700">
                      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                        <div className="flex-1">
                          {booking.status === 'confirmed' && hasTickets && (
                            <>
                              <p className="text-sand-300 font-medium">Your booking is confirmed!</p>
                              <p className="text-sm text-sand-400 mt-1">Click below to view and manage your tickets</p>
                            </>
                          )}
                          {booking.status === 'confirmed' && !hasTickets && (
                            <>
                              <p className="text-sand-300 font-medium">Your booking is confirmed!</p>
                              <p className="text-sm text-sand-400 mt-1">Tickets will be generated shortly</p>
                            </>
                          )}
                          {booking.status === 'pending' && (
                            <p className="text-yellow-400">Booking pending confirmation</p>
                          )}
                          {booking.status === 'cancelled' && (
                            <p className="text-red-400">Booking cancelled</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {hasTickets && (
                            <Button
                              size="md"
                              variant="primary"
                              onClick={() => toggleBooking(booking.id)}
                              className="bg-sand-400 text-earth-900 hover:bg-sand-300 font-medium"
                            >
                              <TicketIcon className="h-5 w-5 mr-2" />
                              View {tickets.filter(t => t.transfer_type !== 'transfer').length} Ticket{tickets.filter(t => t.transfer_type !== 'transfer').length !== 1 ? 's' : ''}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Tickets Section */}
                    {hasTickets && expandedBookings.has(booking.id) && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="mt-6 pt-6 border-t border-earth-700"
                      >
                        <TicketList
                          tickets={tickets}
                          experienceTitle={experience.title}
                          onUpdate={() => {
                            // Refetch tickets for this booking
                            supabase
                              .from('tickets')
                              .select('*')
                              .eq('booking_id', booking.id)
                              .order('ticket_number')
                              .then(({ data }) => {
                                if (data) {
                                  setBookingTickets(prev => ({
                                    ...prev,
                                    [booking.id]: data
                                  }));
                                }
                              });
                          }}
                        />
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              );
            })}

            {/* Show "No bookings" message if user has no bookings of their own */}
            {filteredBookings.length === 0 && transferredTickets.length > 0 && (
              <div className="bg-earth-800/50 rounded-lg p-8 text-center">
                <p className="text-sand-400 mb-2">You don't have any bookings yet.</p>
                <Button onClick={() => navigate('/experiences')} size="sm">
                  Explore Experiences
                </Button>
              </div>
            )}

            {/* Original "No bookings found" for when both are empty */}
            {filteredBookings.length === 0 && transferredTickets.length === 0 && (
              <div className="text-center py-12">
                <p className="text-sand-400 mb-4">No bookings found</p>
                <Button onClick={() => navigate('/experiences')}>
                  Explore Experiences
                </Button>
              </div>
            )}

            {/* Pending Transfer Requests Section */}
            {pendingTransfers.length > 0 && (
              <div className="mt-12 pt-8 border-t border-earth-700">
                <h2 className="text-2xl font-display mb-6 text-sand-100">
                  <span className="inline-flex items-center">
                    <Clock className="h-6 w-6 mr-2" />
                    Pending Transfer Requests
                  </span>
                </h2>
                <div className="space-y-4">
                  {pendingTransfers.map((transfer) => (
                    <motion.div
                      key={transfer.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-yellow-900/20 border-yellow-700/30 rounded-xl p-4 md:p-6 border"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-display text-sand-100 mb-1">
                            {transfer.ticket?.experience?.title || 'Transfer Request'}
                          </h3>
                          <p className="text-sm text-sand-400">
                            Transfer to: <strong>{transfer.to_name || transfer.to_email}</strong>
                          </p>
                          {transfer.reason && (
                            <p className="text-xs text-sand-500 mt-1">
                              Reason: {transfer.reason}
                            </p>
                          )}
                          <p className="text-xs text-sand-500 mt-1">
                            Requested: {new Date(transfer.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
                            <Clock className="h-3 w-3 mr-1" />
                            Awaiting Approval
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => cancelTransfer(transfer.id)}
                            disabled={cancellingTransfer === transfer.id}
                            className="text-red-400 border-red-400 hover:bg-red-400/10"
                          >
                            {cancellingTransfer === transfer.id ? 'Cancelling...' : 'Cancel'}
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Allocated/Transferred Tickets Section - Always show */}
            <div className="mt-12 pt-8 border-t border-earth-700">
              <h2 className="text-2xl font-display mb-6 text-sand-100">
                <span className="inline-flex items-center">
                  <TicketIcon className="h-6 w-6 mr-2" />
                  Tickets Allocated or Transferred to You
                </span>
              </h2>
              
              {transferredTickets.length > 0 ? (
                <div className="space-y-4">
                  {transferredTickets.map((ticket) => {
                    const experience = ticket.experience as any;
                    const isTransferred = ticket.ticketType === 'transfer';
                    const isClaimed = ticket.claimed_by !== null;
                    
                    if (!experience) return null;
                    
                    return (
                      <motion.div
                        key={ticket.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`${
                          isTransferred ? 'bg-blue-900/20 border-blue-700/30' : 'bg-green-900/20 border-green-700/30'
                        } rounded-xl p-4 md:p-6 border`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-display text-sand-100 mb-1">
                              {experience.title}
                            </h3>
                            {/* Add event dates if it's Sunscapes */}
                            {experience.id === '9611812e-270f-4379-8cc9-801ccce37139' && (
                              <p className="text-sm text-sand-400 mb-1">July 10-14th</p>
                            )}
                            <p className="text-sm text-sand-400">
                              {isTransferred 
                                ? 'Permanently transferred to you' 
                                : ticket.originalOwner?.full_name 
                                  ? `Allocated by ${ticket.originalOwner.full_name}`
                                  : 'Allocated to you'}
                            </p>
                            {isClaimed && (
                              <p className="text-xs text-sand-500 mt-1">
                                Claimed on {new Date(ticket.claimed_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                              isTransferred 
                                ? 'bg-blue-500/20 text-blue-400' 
                                : 'bg-green-500/20 text-green-400'
                            }`}>
                              {isTransferred ? 'Transferred' : 'Allocated'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Show QR code for claimed tickets or claim button for unclaimed */}
                        {isClaimed ? (
                          <div className="mt-4">
                            <QRCodeDisplay
                              ticket={ticket}
                              experienceTitle={experience.title}
                              size={120}
                            />
                            
                            {/* Contact organizer button */}
                            <div className="mt-4 flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  window.location.href = `mailto:info@xamanic.letsrendezvous.co?subject=${
                                    isTransferred ? 'Request to Transfer Ticket Again' : 'Question About Allocated Ticket'
                                  }&body=Hi,%0A%0AI have a ${
                                    isTransferred ? 'transferred' : 'allocated'
                                  } ticket for ${experience.title} and would like to ${
                                    isTransferred ? 'transfer it to someone else' : 'ask about my ticket'
                                  }.%0A%0ATicket Details:%0A- Event: ${experience.title}%0A- Current Holder: ${
                                    ticket.guest_name
                                  }%0A%0AThank you!`;
                                }}
                                className="flex-1"
                              >
                                <Mail className="h-4 w-4 mr-2" />
                                {isTransferred ? 'Contact Organizer to Transfer Again' : 'Contact Organizer'}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                            <p className="text-sm text-yellow-400 font-medium mb-2">
                              Action Required: Claim Your Ticket
                            </p>
                            <p className="text-xs text-sand-400 mb-3">
                              This ticket has been {isTransferred ? 'transferred' : 'allocated'} to you. Click below to claim it.
                            </p>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => claimTicket(ticket.id)}
                              disabled={claimingTicket === ticket.id}
                              className="w-full"
                            >
                              {claimingTicket === ticket.id ? 'Claiming...' : 'Claim Ticket'}
                            </Button>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-earth-800/30 rounded-lg p-8 text-center">
                  <p className="text-sand-400 mb-2">No tickets have been allocated or transferred to you yet.</p>
                  <p className="text-sm text-sand-500">When someone shares a ticket with you, it will appear here.</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Contact Modal */}
      <ContactCreatorModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        email={creatorContact.email}
        instagram={creatorContact.instagram}
        phone={creatorContact.phone}
      />
    </div>
  );
}